import convertConfigSummary from '../../../data/convertExperiments.json';
import { FeatureFlag } from './flags';
import { ConvertClientData, ConvertConfigSummary, FeatureFlagService } from './types';

/**
 * Service for retrieving feature flag values from Convert experiments.
 */
class ConvertFeatureFlagService implements FeatureFlagService {
  convertData: ConvertClientData;
  constructor(convertData: ConvertClientData) {
    this.convertData = convertData;
  }

  /**
   * Retrieves the value of a feature flag from the Convert experiments.
   * Our feature flags are determined by the experiment and variation that the client is enrolled in
   * and there may not be a 1-1 mapping between experiments and flags.
   * @param flag - The feature flag to retrieve.
   * @returns The value of the feature flag (true or false), or null if the flag is not present in the Convert experiments.
   */
  getFeatureFlagValue(flag: FeatureFlag) {
    if (!this.convertData.experiments) {
      return null;
    }

    switch (flag) {
      // Fluid browse is enabled when US or UK Fluid + visual browse is set to Fluid browse or Fluid + visual browse
      case FeatureFlag.fluidBrowse:
        return (
          this.isWithinExperimentVariation(
            '[UK] Fluid + visual browse',
            'Fluid browse',
          ) ||
          this.isWithinExperimentVariation(
            '[US] Fluid + visual browse',
            'Fluid browse',
          ) ||
          this.isWithinExperimentVariation(
            '[UK] Fluid + visual browse',
            'Fluid + visual browse',
          ) ||
          this.isWithinExperimentVariation(
            '[US] Fluid + visual browse',
            'Fluid + visual browse',
          )
        );
      // Visual browse is enabled when US or UK Fluid + visual browse is set to visual browse or Fluid + visual browse
      case FeatureFlag.visualCategoryBrowse:
        return (
          this.isWithinExperimentVariation(
            '[UK] Fluid + visual browse',
            'visual browse',
          ) ||
          this.isWithinExperimentVariation(
            '[US] Fluid + visual browse',
            'visual browse',
          ) ||
          this.isWithinExperimentVariation(
            '[UK] Fluid + visual browse',
            'Fluid + visual browse',
          ) ||
          this.isWithinExperimentVariation(
            '[US] Fluid + visual browse',
            'Fluid + visual browse',
          )
        );
      // Price sort is enabled when US or UK Cheapest trips first sort is set to Cheapest trips first
      case FeatureFlag.priceSort:
        return (
          this.isWithinExperimentVariation(
            '[US] Cheapest trips first sort',
            'Cheapest trips first',
          ) ||
          this.isWithinExperimentVariation(
            '[UK] Cheapest trips first sort',
            'Show cheapest first',
          )
        );
      default:
        return null;
    }
  }

  isWithinExperimentVariation(experiment: string, variation: string) {
    const data = convertConfigSummary as ConvertConfigSummary;
    const experimentId = data[experiment].id;
    const variations = data[experiment].variations;
    const variationId = variations[variation];
    if (!experimentId || !variationId) {
      console.error(
        'Experiment or variation not found in Convert experiments for %s or %s',
        experiment,
        variation,
      );
      return false;
    }
    const isInExperiment =
      this.convertData.experiments[experimentId]?.variation_id === variationId;
    console.log(
      'Client %s enrolled in experiment "%s" (%s) for variation "%s" (%s)',
      isInExperiment ? 'is' : 'is not',
      experiment,
      experimentId,
      variation,
      variationId,
    );
    return isInExperiment;
  }
}

export default ConvertFeatureFlagService;
